import React from "react";
import debug from "debug";
import { isEmpty } from "lodash";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { Chip, Paper, Popover } from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/PhoneLocked";
import { CheckDecagram } from "mdi-material-ui";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import {
  ConsultantCardFragment,
  ConsultantSearchCardFragment,
  NodeType,
  RemoveConsultantAvatar,
  UploadConsultantAvatar
} from "@graphql";

import { WithTooltip } from "@components";
import ItemCard from "@components/ItemCard/ItemCard";
import NodeCallList from "@containers/NodeCallList/NodeCallList";
import CardDetails, {
  emailToDetail,
  phoneToDetails,
  lastContactToDetail,
  responsibleUserToDetail,
  availabilityToDetail,
  ssnToDetail,
  claimToDetail,
  prescriptionCodeToDetail
} from "@components/ItemCard/CardDetails";

const log = debug("app:components:ConsultantCardContent");

type Consultant = ConsultantCardFragment & Partial<ConsultantSearchCardFragment>;

type DetailOptions =
  | "availability"
  | "email"
  | "phone"
  | "lastContact"
  | "responsibleUser"
  | "ssn"
  | "employeeSalaryClaim"
  | "subcontractorSalaryClaim"
  | "prescriptionCode";

export type ConsultantCardContentProps = {
  flat?: boolean;
  details?: DetailOptions[];
  highlightSearch?: string;
  item: Consultant;
  onClick?: (e: React.SyntheticEvent<any>) => any;
  onTitleClick?: (e: React.SyntheticEvent<any>) => any;
  onEdit?: (e: React.SyntheticEvent<any>) => any;
  onResponsiblePersonClick?: (e: React.SyntheticEvent<any>) => void;
  selected?: boolean;
  style?: React.CSSProperties;
  withAvatarUpload?: boolean;
  withCallLists?: boolean; // show call list star
};

@observer
export class ConsultantCardContentBase extends React.Component<ConsultantCardContentProps> {
  static defaultProps: Partial<ConsultantCardContentProps> = {
    details: []
  };

  @observable isCallListPopoverOpen = false;

  toDetail = (detail: DetailOptions) => {
    const item = this.props.item;

    // replace employeeSalaryClaim|subcontractorSalaryClaim to prescriptionCode only for doctors
    if (
      item.Professions?.includes("Läkare")
        ? ["employeeSalaryClaim", "subcontractorSalaryClaim"].includes(detail)
        : detail === "prescriptionCode"
    ) {
      return null;
    }

    switch (detail) {
      case "availability":
        return availabilityToDetail(item.Availability ? item.Availability.ranges : []);
      case "email":
        return emailToDetail(item.Email, item.secondaryEmail);
      case "phone":
        return phoneToDetails(item.CellPhone || item.Phone);
      case "lastContact":
        return lastContactToDetail(item.LastContactDate);
      case "responsibleUser":
        return responsibleUserToDetail(item.ResponsiblePerson && item.ResponsiblePerson);
      case "ssn":
        return ssnToDetail(item.SocialSecurityNo);
      case "employeeSalaryClaim":
        return claimToDetail(item?.contactType?.employeeSalaryClaim, "Löneanspråk");
      case "subcontractorSalaryClaim":
        return claimToDetail(
          item?.contactType?.subcontractorSalaryClaim,
          "Ersättningsanspråk"
        );
      case "prescriptionCode":
        return prescriptionCodeToDetail(item.PrescriptionCode, locale.PrescriptionCode);
    }
  };

  handleAvatarClick = () => {
    const { item, withAvatarUpload } = this.props;

    if (!withAvatarUpload) {
      return;
    }

    store()
      .upload.requestImageUploadAsUrl()
      .then(store().upload.requestImageCrop)
      .then((file) => {
        if (file) {
          UploadConsultantAvatar.func({
            id: item._id,
            file
          });
        }
      })
      .catch((e) => {
        store().snackbars.error(`Uploading avatar failed\nReason: ${e}`);
      });
  };

  handeAvatarRemove = () => {
    const { item } = this.props;

    RemoveConsultantAvatar.func({ id: item._id }).catch((e) => {
      store().snackbars.error(`Operation failed\nReason: ${e}`);
    });
  };

  renderCallListsPopper = () => {
    const { item } = this.props;

    if (!this.isCallListPopoverOpen) {
      return null;
    }

    const component = (
      <Paper>
        <NodeCallList
          node={{ NodeID: item._id, NodeType: NodeType.Consultant, Name: item.Name }}
        />
      </Paper>
    );

    return (
      <Popover
        open
        anchorEl={store().ui.callListsPopperAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => {
          this.isCallListPopoverOpen = false;
        }}
      >
        {component}
      </Popover>
    );
  };

  render() {
    const { item, details, withCallLists, withAvatarUpload, ...rest } = this.props;
    log(`renderCard ${item && item.Name}`, { item });

    const _details = !isEmpty(details) ? (
      <CardDetails
        items={(details || []).map(this.toDetail)}
        gridSettings={{
          lg: { min: 4, max: 4 }
        }}
      />
    ) : null;

    return (
      <>
        <ItemCard
          avatarTooltip={withAvatarUpload ? locale["Click to upload a new image"] : null}
          avatarDeleteTooltip={
            withAvatarUpload ? locale["Click to delete an avatar"] : null
          }
          onAvatarClick={this.handleAvatarClick}
          onAvatarRemove={this.handeAvatarRemove}
          error={item.Unfit}
          entity={this.props.item}
          details={_details}
          flat={this.props.flat}
          subTitle={item.Address ? item.Address.City : ""}
          chips={
            <>
              {item.isQualityApproved && (
                <WithTooltip
                  title={
                    item.isQualityExpired
                      ? locale.IsQualifiedAndExpired
                      : locale.IsQualified
                  }
                  placement="top"
                >
                  <CheckDecagram
                    color={item.isQualityExpired ? "primary" : "secondary"}
                  />
                </WithTooltip>
              )}
              {item.Unfit && <Chip icon={<ErrorIcon />} label={locale.UnfitConsultant} />}
            </>
          }
          keyValues={[
            { key: locale.Expertise, value: item.AreasOfExpertise.join(", ") },
            { key: locale.Specialization, value: item.Specializations.join(", ") },
            {
              key: locale["Known systems"],
              value: item.expertise.systems.join(", ")
            },
            { key: locale.Machines, value: item.expertise.machines.join(", ") }
          ]}
          responsiblePerson={!details.includes("responsibleUser")}
          showStar={null}
          onStarClick={(e) => {
            e.stopPropagation();
            store().ui.callListsPopperAnchor = e.currentTarget
              .closest("div")
              .getElementsByTagName("span")[0];
            this.isCallListPopoverOpen = true;
            // after opening and closing an overlay, @observer stops working
            // difficult to say why.. so forceUpdate "fixes" the problem
            this.forceUpdate();
          }}
          {...rest}
        />

        {this.renderCallListsPopper()}
      </>
    );
  }
}
